import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import './styles/App.css';
import Sidebar from './components/Sidebar';
import TopHeader from './components/TopHeader';
import GracePeriodBanner from './components/GracePeriodBanner';
import PlanGate from './components/PlanGate';
import Home from './pages/Home';
import Balances from './pages/Balances';
import About from './pages/About';
import Exchanges from './pages/Exchanges';
import Symbols from './pages/Symbols';
import FundingFee from './pages/FundingFee';
import TakerMakerFee from './pages/TakerMakerFee';
import Opportunities from './pages/Opportunities';
import Trades from './pages/Trades';
import TradeDetailPage from './pages/TradeDetailPage';
import ChartPage from './pages/ChartPage';
import Settings from './pages/Settings';
import Login from './pages/Login';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FundingRateNotifications from './pages/FundingRateNotifications';
import Admin from './pages/Admin';
import RedisMonitor from './pages/RedisMonitor';
import OrderbookStreaming from './pages/OrderbookStreaming';
import HedgeOpportunities from './pages/HedgeOpportunities';
import Education from './pages/Education';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import { AuthProvider } from './context/AuthContext';
import { initGA, trackPageView } from './utils/analytics';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID_HERE';

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    if (localStorage.getItem('cookieConsent') === 'accepted') {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);
  return null;
}

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('cookieConsent') === 'accepted') {
      initGA();
    }
  }, []);

  const handleCookieAccept = () => {
    initGA();
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <HelmetProvider>
          <Router>
            <AnalyticsTracker />
            <div className="App">
              <CookieConsent onAccept={handleCookieAccept} />
              <Sidebar isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
              <div className="main-content">
                <TopHeader isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
                <GracePeriodBanner />
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={<Home />} />
                  <Route path="/balances" element={<ProtectedRoute><Balances /></ProtectedRoute>} />
                  <Route path="/about" element={<About />} />
                  <Route path="/exchanges" element={<Exchanges />} />
                  <Route path="/symbols" element={<Symbols />} />
                  <Route path="/funding-fees" element={<FundingFee />} />
                  <Route path="/taker-maker" element={<TakerMakerFee />} />
                  <Route path="/opportunities" element={
                    <ProtectedRoute>
                      <PlanGate feature="opportunities"><Opportunities /></PlanGate>
                    </ProtectedRoute>
                  } />
                  <Route path="/trades" element={
                    <ProtectedRoute>
                      <PlanGate feature="trades"><Trades /></PlanGate>
                    </ProtectedRoute>
                  } />
                  <Route path="/trades/:tradeId" element={
                    <ProtectedRoute>
                      <PlanGate feature="trades"><TradeDetailPage /></PlanGate>
                    </ProtectedRoute>
                  } />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/chart" element={<ChartPage />} />
                  <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
                  <Route path="/subscription/cancel" element={<Login />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/notifications/funding-rate" element={
                    <ProtectedRoute>
                      <PlanGate feature="notifications"><FundingRateNotifications /></PlanGate>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/admin/scripts" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/admin/redis" element={<ProtectedRoute><RedisMonitor /></ProtectedRoute>} />
                  <Route path="/admin/orderbook-streaming" element={<ProtectedRoute><OrderbookStreaming /></ProtectedRoute>} />
                  <Route path="/admin/hedge-opportunities" element={<ProtectedRoute><HedgeOpportunities /></ProtectedRoute>} />
                  <Route path="/education" element={<Education />} />
                </Routes>
              </div>
            </div>
          </Router>
        </HelmetProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
